import type { ShipmentRate, ShippingAddressType } from "@constants/formDefaults";
import type { CartInfo } from "./getCartInfo";

export type UpdatePurchasePayload = {
  uuid: string;
  items?: Array<{ sku: string; quantity: number }>;
  coupon_id?: string;
  promotion_code?: string;
  billing_address?: {
    postal_code: string;
  };
  shipping_address?: ShippingAddressType;
  shipment_rate?: ShipmentRate;
  stripe_payment_method_id?: string;
  professional_credentials?: string;
};

export type UpdatePurchaseResponse = {
  email?: string;
  stripe_invoice?: {
    tax?: number;
    automatic_tax?: {
      enabled: boolean;
      status: string;
    };
  };
  cart_info: CartInfo;
  stripe_invoice_id?: string;
  totalTax?: number;
  updateError?: string | boolean;
};

export async function updatePurchase(payload: UpdatePurchasePayload): Promise<{
  data: UpdatePurchaseResponse;
  error?: string;
}> {
  const query = `
    mutation UPDATE_PURCHASE($uuid: String!, $params: UpdatePurchaseInput!) {
      update_purchase(uuid: $uuid, params: $params) {
        email
        stripe_invoice
        stripe_invoice_id
        cart_info {
          currency
          subtotal
          total_amount
          applied_discounts {
            discount_amount
            description
            coupon_id
          }
          tax
          items {
            product_title
            product_description
            sku
            frequency
            quantity
            price_amount
            unit_amount
            type
          }
          bundles {
            product_title
            product_description
            sku
            frequency
            quantity
            price_amount
            unit_amount
            type
          }
        }
      }
    }
  `;

  const { uuid, ...params } = payload;

  const res = await fetch(import.meta.env.VITE_PURCHASE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      operationName: null,
      query,
      variables: { uuid, params },
    }),
  });

  const json = await res.json();

  if (json.errors?.length) {
    throw new Error(`${json.errors[0].message}. Code: update_purchase`);
  }

  const update = json?.data?.update_purchase || {};

  const rawStripeTax = update?.stripe_invoice?.tax; // Stripe returns amounts in cents
  const taxEnabled = update?.stripe_invoice?.automatic_tax?.enabled === true;
  const taxComplete = update?.stripe_invoice?.automatic_tax?.status === "complete";

  const updateError =
    (taxEnabled &&
      !taxComplete &&
      `Unable to calculate tax. Please verify your zip code and try again.`) ||
    undefined;

  // Ensure cart_info.tax is only set when Stripe automatic tax is enabled and complete
  const computedTax =
    taxEnabled && taxComplete && typeof rawStripeTax === "number" ? rawStripeTax / 100 : undefined;
  const transformedCartInfo = update?.cart_info
    ? {
        ...update.cart_info,
        tax: computedTax,
        // Preserve server-provided items and bundles for display
        items: update.cart_info.items ?? [],
        bundles: update.cart_info.bundles ?? [],
      }
    : update?.cart_info;

  return {
    data: {
      ...update,
      cart_info: transformedCartInfo,
      totalTax: computedTax,
      updateError: updateError || undefined,
    },
  };
}
